import { useIntegrationApp } from "@membranehq/react";
import { AlertTriangle, Check, ChevronDown, ChevronRight, Pencil, Trash2, Zap } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useHubspotIntegration,
  useSyncConfigurations,
  useDeleteSyncConfiguration,
  useUpdateSyncConfiguration,
} from "../../../hooks/QueryHooks";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getProvider } from "../../../integrations/providers";
import type { ProviderObjectSlug } from "../../../integrations/providers/types";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import IntegrationDetailShell from "../../home/settings/integrations/components/IntegrationDetailShell";
import useWorkspaceStore from "../../../store/workspace/workspaceStore";
import { MappingStudio } from "../shared/mapping-studio";
import ProviderNewFlow from "../shared/new-flow/ProviderNewFlow";
import { normalizeSyncDirection, toUiSlug } from "../shared/mapping-studio/adapters";
import { getFieldMappings } from "../../../services/syncConfiguration/syncConfigurationEndpoints";

const HubspotPage = () => {
  const workspace: any = useWorkspaceStore((state) => state.workspace);
  const integrationApp = useIntegrationApp();
  const provider = getProvider("hubspot");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const useStabilizedMappingStudio = (import.meta.env.VITE_ENABLE_CRM_MAPPING_STUDIO_STABILIZATION ?? "true") !== "false";
  const activatableObjects = useMemo(
    () => [...(provider.activatableObjects || [])],
    [provider.activatableObjects],
  );
  const syncConfigBackedObjects = useMemo(
    () => new Set(["accounts", "contacts", "companies"]),
    [],
  );
  const nonCoreObjects = useMemo(
    () => activatableObjects.filter((slug) => !syncConfigBackedObjects.has(slug)),
    [activatableObjects, syncConfigBackedObjects],
  );

  const [integrationLoading, setIntegrationLoading] = useState<boolean>(false);
  const [showMappingStudio, setShowMappingStudio] = useState<boolean>(false);
  const [deletingObjectKey, setDeletingObjectKey] = useState<string | null>(null);
  const [syncingObjectKey, setSyncingObjectKey] = useState<string | null>(null);
  const [expandedObjects, setExpandedObjects] = useState<Record<string, boolean>>({});
  const [mappedFieldCountBySyncId, setMappedFieldCountBySyncId] = useState<Record<string, number>>({});

  const deleteSyncConfigMutation = useDeleteSyncConfiguration();
  const updateSyncConfigMutation = useUpdateSyncConfiguration();

  const { data: hubspotIntegration }: any = useHubspotIntegration({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  const { data: syncConfigurations }: any = useSyncConfigurations({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id && !!hubspotIntegration?.length && !showMappingStudio,
  });

  const hubspotSyncConfigurations = useMemo(() => (
    syncConfigurations?.filter((config: any) => config && config.integration_id === hubspotIntegration?.[0]?.id) || []
  ), [hubspotIntegration, syncConfigurations]);

  const { data: connectionState, isLoading: isCheckingConnection, refetch: refetchConnectionState } = useQuery({
    queryKey: ["onboarding-provider-connection", workspace?.id, provider.key],
    enabled: !!workspace?.id,
    retry: 2,
    retryDelay: (attemptIndex) => (attemptIndex === 0 ? 400 : 1200),
    queryFn: async () => {
      const integration = await integrationApp.integration(provider.key).get();
      const existingConnection = integration?.connection;
      if (existingConnection && !existingConnection.disconnected) {
        return {
          isConnected: true,
          connectionData: { ...integration, connectionId: existingConnection?.id || integration?.id },
          connectedAt: (existingConnection as any)?.connectedAt
            || (integration as any)?.createdAt
            || (integration as any)?.updatedAt
            || new Date().toISOString(),
        };
      }

      return {
        isConnected: false,
        connectionData: null,
        connectedAt: null,
      };
    },
  });

  const isConnected = connectionState?.isConnected ?? false;
  const connectionData = connectionState?.connectionData ?? null;
  const connectedAt = connectionState?.connectedAt ?? null;

  const { data: nonCoreFlowEnabledBySlug = {} } = useQuery({
    queryKey: [
      "hubspot-non-core-flow-enabled",
      workspace?.id,
      connectionData?.connectionId || connectionData?.id,
      nonCoreObjects.join("|"),
    ],
    enabled: !!workspace?.id && isConnected && nonCoreObjects.length > 0 && !showMappingStudio,
    staleTime: 0,
    refetchOnMount: "always",
    queryFn: async () => {
      const statusBySlug: Record<string, boolean> = {};

      await Promise.all(
        nonCoreObjects.map(async (slug) => {
          const firstFlowKey = provider.flowKeys.firstSync?.[slug as ProviderObjectSlug];
          const receiveFlowKey = provider.flowKeys.receive?.[slug as ProviderObjectSlug];
          if (!firstFlowKey || !receiveFlowKey) {
            statusBySlug[slug] = false;
            return;
          }

          const [firstFlowResult, receiveFlowResult] = await Promise.allSettled([
            integrationApp.connection(provider.key).flow(firstFlowKey).get(),
            integrationApp.connection(provider.key).flow(receiveFlowKey).get(),
          ]);

          const firstEnabled = firstFlowResult.status === "fulfilled" && !!firstFlowResult.value?.enabled;
          const receiveEnabled = receiveFlowResult.status === "fulfilled" && !!receiveFlowResult.value?.enabled;
          statusBySlug[slug] = firstEnabled || receiveEnabled;
        }),
      );

      return statusBySlug;
    },
  });

  const syncObjectRows = useMemo(() => {
    const labels: Record<string, string> = {
      accounts: "Accounts",
      contacts: "Contacts",
      companies: "Companies",
      deals: "Deals",
      notes: "Notes",
      tasks: "Tasks",
      emails: "Emails",
    };
    return activatableObjects.map((slug) => {
      const inboundSync = hubspotSyncConfigurations.find((sync: any) => (
        normalizeSyncDirection(sync?.sync_direction) === "write_in" && toUiSlug(sync?.destination_object) === slug
      ));
      const writebackSync = hubspotSyncConfigurations.find((sync: any) => (
        normalizeSyncDirection(sync?.sync_direction) === "write_out" && toUiSlug(sync?.destination_object) === slug
      ));
      const flowEnabled = !!nonCoreFlowEnabledBySlug[slug];
      const enabled = syncConfigBackedObjects.has(slug)
        ? inboundSync?.status === "enabled"
        : flowEnabled;
      return {
        key: slug,
        label: labels[slug] || slug,
        inboundSync,
        writebackSync,
        configured: Boolean(inboundSync || writebackSync),
        enabled,
        writebackEnabled: writebackSync?.status === "enabled",
      };
    });
  }, [activatableObjects, hubspotSyncConfigurations, nonCoreFlowEnabledBySlug, syncConfigBackedObjects]);

  useEffect(() => {
    if (!workspace?.id) {
      setMappedFieldCountBySyncId({});
      return;
    }

    const inboundSyncIds = syncObjectRows
      .map((row) => row.inboundSync?.id)
      .filter(Boolean) as string[];

    if (!inboundSyncIds.length) {
      setMappedFieldCountBySyncId({});
      return;
    }

    let cancelled = false;
    const systemFields = new Set(["hubspot_url", "hubspot_record_id"]);

    (async () => {
      const next: Record<string, number> = {};
      await Promise.all(inboundSyncIds.map(async (syncId) => {
        try {
          const response = await getFieldMappings({ workspace_id: workspace.id, sync_id: syncId });
          const mappings = Array.isArray((response as any)?.data) ? (response as any).data : [];
          next[syncId] = mappings.filter((mapping: any) => (
            !mapping?.is_owner_assignment
            && !mapping?.is_relationship_assignment
            && !systemFields.has(mapping?.source_field_name)
          )).length;
        } catch {
          next[syncId] = 0;
        }
      }));

      if (!cancelled) {
        setMappedFieldCountBySyncId(next);
      }
    })().catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [workspace?.id, syncObjectRows]);

  const formatRelativeSync = (dateValue?: string | null) => {
    if (!dateValue) return null;
    const timestamp = new Date(dateValue).getTime();
    if (!Number.isFinite(timestamp)) return null;
    const now = Date.now();
    const diffMs = now - timestamp;
    const absMinutes = Math.round(Math.abs(diffMs) / 60000);

    if (absMinutes < 1) return "just now";
    if (absMinutes < 60) return `${absMinutes}m ago`;
    const absHours = Math.round(absMinutes / 60);
    if (absHours < 24) return `${absHours}h ago`;
    const absDays = Math.round(absHours / 24);
    if (absDays < 30) return `${absDays}d ago`;
    return new Date(timestamp).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const detailedSyncRows = useMemo(() => syncObjectRows.map((row) => {
    const inboundSync = row.inboundSync;
    const writebackSync = row.writebackSync;
    const mappedFields = inboundSync?.id ? (mappedFieldCountBySyncId[inboundSync.id] || 0) : 0;
    const hasError = inboundSync?.status === "error" || writebackSync?.status === "error";
    const lastSyncLabel = formatRelativeSync(inboundSync?.updated_at || inboundSync?.full_sync_triggerred_at || null);
    return {
      ...row,
      errors: hasError ? 1 : 0,
      mappedFields,
      lastSyncLabel,
      matchKey: inboundSync?.matcher_field_name || "external_id",
      canExpand: row.configured,
    };
  }), [mappedFieldCountBySyncId, syncObjectRows]);

  const ensureInfra = async () => {
    try {
      await Promise.allSettled(
        activatableObjects.map((slug) =>
          integrationApp.connection(provider.key).dataSource(slug).get({ autoCreate: true }),
        ),
      );

      await Promise.allSettled(
        activatableObjects.map((slug) =>
          integrationApp.connection(provider.key).fieldMapping(slug).get({ autoCreate: true }),
        ),
      );

      const firstSyncFlows = Array.from(
        new Set(
          activatableObjects
            .map((slug) => provider.flowKeys.firstSync?.[slug])
            .filter((key): key is string => Boolean(key)),
        ),
      );
      await Promise.allSettled(
        firstSyncFlows.map((flowKey) =>
          integrationApp.connection(provider.key).flow(flowKey).get({ autoCreate: true }),
        ),
      );
    } catch (error) {
      // best-effort provisioning
      console.error("Failed to ensure HubSpot data sources/field mappings", error);
    }
  };

  const handleConnectOrReconnect = async () => {
    setIntegrationLoading(true);
    try {
      const connection = isConnected
        ? await integrationApp.integration(provider.key).openNewConnection()
        : await integrationApp.integration(provider.key).connect();

      if (connection && (connection as any).state === "READY") {
        await ensureInfra();
        await refetchConnectionState();
      }
    } catch (error) {
      console.error("HubSpot connection failed:", error);
    } finally {
      setIntegrationLoading(false);
    }
  };

  const handleBack = () => {
    if (!workspace?.unique_identifier) {
      navigate(-1);
      return;
    }
    navigate(`/home/${workspace.unique_identifier}/settings/integrations`);
  };

  const handleOpenMappingStudio = () => {
    setShowMappingStudio(true);
  };

  const handleCloseMappingStudio = () => {
    setShowMappingStudio(false);
    if (workspace?.id) {
      queryClient.invalidateQueries({ queryKey: ["sync-configurations", workspace.id] }).catch(() => undefined);
    }
    queryClient.invalidateQueries({ queryKey: ["hubspot-non-core-flow-enabled"] }).catch(() => undefined);
    refetchConnectionState().catch(() => undefined);
  };

  const toggleExpand = (key: string) => {
    setExpandedObjects((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDeleteObjectSync = async (objectKey: string) => {
    if (!workspace?.id) return;
    const objectSlug = objectKey as ProviderObjectSlug;
    const row = syncObjectRows.find((item) => item.key === objectKey);
    if (!row) return;
    const syncIds = [row.writebackSync?.id, row.inboundSync?.id].filter(Boolean) as string[];
    if (!syncIds.length) return;

    setDeletingObjectKey(objectKey);
    try {
      for (const syncId of syncIds) {
        await updateSyncConfigMutation.mutateAsync({
          workspace_id: workspace.id,
          sync_id: syncId,
          data: { status: "disabled" },
        }).catch(() => undefined);
      }

      const flowKeys = [
        provider.flowKeys.firstSync?.[objectSlug],
        provider.flowKeys.receive?.[objectSlug],
        provider.flowKeys.send?.[objectSlug],
      ].filter((key): key is string => Boolean(key));

      await Promise.allSettled(
        flowKeys.map((flowKey) => (
          integrationApp
            .connection(provider.key)
            .flow(flowKey)
            .patch({ enabled: false })
        )),
      );

      for (const syncId of syncIds) {
        await deleteSyncConfigMutation.mutateAsync({
          workspace_id: workspace.id,
          sync_id: syncId,
        });
      }

      toast.success("Sync deleted", {
        description: `${row.label} sync configuration has been removed.`,
      });
    } catch (error) {
      console.error(`Failed to delete ${objectKey} sync`, error);
      toast.error("Delete failed", {
        description: `Could not delete ${row.label} sync. Please try again.`,
      });
    } finally {
      setDeletingObjectKey(null);
    }
  };

  const handleSyncNow = async (objectKey: string) => {
    const row = detailedSyncRows.find((item) => item.key === objectKey);
    if (!row?.inboundSync?.id) {
      toast.warning("Sync not configured", {
        description: "Please complete configuration before running sync.",
      });
      return;
    }

    const objectSlug = objectKey as ProviderObjectSlug;
    const firstSyncFlowKey = provider.flowKeys.firstSync?.[objectSlug];
    if (!firstSyncFlowKey) {
      toast.error("Unsupported object", {
        description: `${row.label} sync is not available.`,
      });
      return;
    }

    setSyncingObjectKey(objectKey);
    try {
      await integrationApp.connection(provider.key).flow(firstSyncFlowKey).get({ autoCreate: true });
      await integrationApp.connection(provider.key).flow(firstSyncFlowKey).patch({
        enabled: true,
        parameters: { sync_id: row.inboundSync.id },
      });
      await integrationApp.connection(provider.key).flow(firstSyncFlowKey).run({ input: null });
      toast.success("Sync started", {
        description: `${row.label} sync run has been triggered.`,
      });
    } catch (error) {
      console.error(`Failed to start sync for ${objectKey}`, error);
      toast.error("Sync failed", {
        description: `Could not trigger ${row.label} sync right now.`,
      });
    } finally {
      setSyncingObjectKey(null);
    }
  };

  const connectedDateLabel = connectedAt
    ? new Date(connectedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;

  // Show Mapping Studio as full-page view when opened
  if (showMappingStudio) {
    return (
      <div className="max-w-4xl mx-auto py-6 px-4">
        {isCheckingConnection ? (
          <div className="py-10 text-center text-sm text-[var(--mantine-color-dimmed)]">Checking connection...</div>
        ) : (
          useStabilizedMappingStudio ? (
            <MappingStudio
              providerKey="hubspot"
              isConnected={isConnected}
              connectionId={connectionData?.connectionId || connectionData?.id}
              singleSyncCta
              showDeleteActions={false}
              onBack={handleCloseMappingStudio}
            />
          ) : (
            <ProviderNewFlow
              providerKey="hubspot"
              canonicalObjectSlugs={activatableObjects}
              existingSyncConfigurations={hubspotSyncConfigurations}
              isConnected={isConnected}
              connectionId={connectionData?.connectionId || connectionData?.id}
            />
          )
        )}
      </div>
    );
  }

  return (
    <IntegrationDetailShell
      integrationName={provider.label}
      connected={isConnected}
      credentialOk={isConnected}
      externalId={connectionData?.id || hubspotIntegration?.[0]?.id || null}
      lastSync={connectedDateLabel}
      reconnectLoading={integrationLoading || isCheckingConnection}
      reconnectLabel={isConnected ? "Reconnect" : "Connect"}
      onReconnect={handleConnectOrReconnect}
      onBack={handleBack}
      showMappingStudioCta
      setupComplete={!!hubspotIntegration?.[0]?.setup_complete}
      onOpenMappingStudio={handleOpenMappingStudio}
    >
      {!isConnected ? (
        <div className="rounded-lg border border-solid border-[var(--mantine-color-default-border)] p-4">
          <p className="text-sm text-[var(--mantine-color-text)]">
            Connect HubSpot to start syncing accounts, contacts, deals, notes, tasks, and emails.
          </p>
          <p className="mt-1 text-xs text-[var(--mantine-color-dimmed)]">
            You can configure accounts, contacts, deals, notes, tasks, and emails.
          </p>
          <p className="mt-1 text-xs text-[var(--mantine-color-dimmed)]">Once connected, complete sync mappings below.</p>
        </div>
      ) : (
        <div>
          <h2 className="mb-3 text-[13px] font-medium text-foreground">Sync Objects</h2>
          <div className="rounded-lg border border-border divide-y divide-border">
            {detailedSyncRows.map((row) => (
              <div key={row.key}>
                <button
                  type="button"
                  onClick={() => toggleExpand(row.key)}
                  className="flex w-full items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {expandedObjects[row.key] ? (
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                    <span className="text-[13px] font-medium text-foreground">{row.label}</span>
                    {!row.enabled && (
                      <span className="text-[11px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">Disabled</span>
                    )}
                  </div>

                  <div className="flex items-center gap-4 text-[11px] text-muted-foreground">
                    {row.enabled ? (
                      <>
                        {row.errors > 0 ? (
                          <div className="flex items-center gap-1 text-status-cancelled">
                            <AlertTriangle className="h-3 w-3" />
                            <span>{row.errors} errors</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-status-active">
                            <Check className="h-3 w-3" />
                            <span>Healthy</span>
                          </div>
                        )}
                        <span>{row.mappedFields} fields</span>
                        {row.lastSyncLabel ? <span>Synced {row.lastSyncLabel}</span> : null}
                      </>
                    ) : null}
                  </div>
                </button>

                {expandedObjects[row.key] && row.configured ? (
                  <div className="px-4 pb-3 ml-7 space-y-2">
                    <div className="flex items-center gap-3 text-[12px]">
                      <div className="flex items-center gap-1.5">
                        <span className="text-muted-foreground">Match key:</span>
                        <span className="text-foreground">{row.matchKey}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-muted-foreground">Write-back:</span>
                        <span className="text-foreground">{row.writebackEnabled ? "On" : "Off"}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-6 text-[11px] px-2"
                        onClick={() => {
                          setShowMappingStudio(true);
                        }}
                      >
                        <Pencil className="h-3 w-3 mr-1" />
                        Edit fields
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-6 text-[11px] px-2"
                        disabled={syncingObjectKey === row.key}
                        onClick={() => { handleSyncNow(row.key).catch(console.error); }}
                      >
                        <Zap className="h-3 w-3 mr-1" />
                        {syncingObjectKey === row.key ? "Syncing..." : "Sync now"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-6 text-[11px] px-2 text-status-cancelled"
                        disabled={deletingObjectKey === row.key}
                        onClick={() => { handleDeleteObjectSync(row.key).catch(console.error); }}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        {deletingObjectKey === row.key ? "Deleting..." : "Delete"}
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}
    </IntegrationDetailShell>
  );
};

export default HubspotPage;
